import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatHeureMur, formatJourMurCourt, maintenantMurCasablanca } from '@/lib/dates';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

type TvIntervention = {
  id: string;
  nature: string;
  statut: string;
  urgent: boolean;
  client: { prenom: string; nom: string };
  moto: { modele: string; vin: string };
  mecanicien: { id: string; prenom: string; nom: string; couleur: string };
  creneauDebut: string;
  creneauFin: string;
  dureeEstimeeMinutes: number;
};

type TodayMecano = {
  mecanicien: { id: string; prenom: string; nom: string; couleur: string };
  timeline: Array<{ id: string; debut: string; fin: string; nature: string; statut: string }>;
};

/** Une intervention TV est "urgente visuelle" si flag urgent et pas encore livrée/annulée */
function isTvUrgent(i: TvIntervention): boolean {
  return i.urgent && i.statut !== 'LIVRE' && i.statut !== 'ANNULE';
}

/**
 * Écran TV atelier — mode kiosk full-screen dark mode.
 * Route publique avec ?token=<jwt kiosk>.
 * Référence : STITCH/cran_atelier_tv_temps_r_el_dark_mode/
 */
export default function TvDisplayPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [now, setNow] = useState(maintenantMurCasablanca);

  useEffect(() => {
    const i = setInterval(() => setNow(maintenantMurCasablanca()), 1000);
    return () => clearInterval(i);
  }, []);

  const today = now.toISOString().slice(0, 10);

  const interventionsQuery = useQuery({
    queryKey: ['tv', 'interventions', today],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/interventions?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { interventions: TvIntervention[] };
    },
    enabled: !!token,
    refetchInterval: 15_000,
  });

  const statsQuery = useQuery({
    queryKey: ['tv', 'stats'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/stats/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as { chargeByMecano: TodayMecano[] };
    },
    enabled: !!token,
    refetchInterval: 15_000,
  });

  if (!token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-3">Token requis</h1>
          <p className="text-gray-400">
            Accède à cette page avec <code>?token=...</code> généré par l'admin.
          </p>
        </div>
      </div>
    );
  }

  const interventions = interventionsQuery.data?.interventions ?? [];
  const chargeByMecano = statsQuery.data?.chargeByMecano ?? [];

  // La TV ne doit JAMAIS présenter des données périmées comme « live ».
  // En erreur réseau, l'indicateur passe en rouge et un bandeau l'annonce.
  const hasError = interventionsQuery.isError || statsQuery.isError;
  const isSyncing = interventionsQuery.isFetching || statsQuery.isFetching;
  const syncDotClass = hasError
    ? 'bg-red-500 animate-pulse'
    : isSyncing
      ? 'bg-yellow-500 animate-pulse'
      : 'bg-green-500';
  const syncLabel = hasError ? 'Hors ligne' : 'Sync live';

  return (
    <div className="min-h-screen bg-[#0F0F0F] text-white p-6 font-sans">
      <header className="flex items-center justify-between pb-4 mb-6 border-b-2 border-[#FF6600]">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#FF6600]">KTM ATELIER MAROC</h1>
          <p className="text-sm text-gray-400 mt-1 uppercase tracking-wider">Casablanca · Temps réel</p>
        </div>
        <div className="text-right">
          <div className="text-7xl font-bold font-mono leading-none">
            {String(now.getUTCHours()).padStart(2, '0')}:
            {String(now.getUTCMinutes()).padStart(2, '0')}
          </div>
          <div className="text-sm text-gray-400 mt-2 font-mono uppercase tracking-wider">
            {formatJourMurCourt(now)}
          </div>
        </div>
        <div
          className={`flex items-center gap-2 text-sm ${hasError ? 'text-red-400' : 'text-gray-400'}`}
        >
          <span className={`w-2.5 h-2.5 rounded-full ${syncDotClass}`} />
          {syncLabel}
        </div>
      </header>

      {hasError && (
        <div
          role="alert"
          className="mb-6 px-4 py-3 rounded-md bg-red-950 border border-red-700 text-red-200 text-sm font-medium"
        >
          Connexion perdue — données possiblement périmées. Reconnexion en cours…
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6">
        {chargeByMecano.map((c) => {
          const enCours = interventions.find(
            (i) => i.mecanicien.id === c.mecanicien.id && i.statut === 'EN_COURS',
          );
          const aVenir = interventions
            .filter(
              (i) =>
                i.mecanicien.id === c.mecanicien.id &&
                ['CONFIRME', 'EN_ATTENTE'].includes(i.statut) &&
                new Date(i.creneauDebut).getTime() > now.getTime(),
            )
            .sort((a, b) => new Date(a.creneauDebut).getTime() - new Date(b.creneauDebut).getTime())
            .slice(0, 2);

          return (
            <div key={c.mecanicien.id} className="bg-[#1A1A1A] rounded-lg p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="w-12 h-12 rounded-md flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: c.mecanicien.couleur }}
                >
                  {c.mecanicien.prenom.charAt(0)}
                </span>
                <div className="flex-1">
                  <div className="text-2xl font-semibold">
                    {c.mecanicien.prenom} {c.mecanicien.nom}
                  </div>
                </div>
                {enCours ? (
                  <span className="px-3 py-1 rounded-sm bg-[#D97706] text-white text-xs font-bold uppercase tracking-wide">
                    En cours
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-sm bg-green-700 text-white text-xs font-bold uppercase tracking-wide">
                    Disponible
                  </span>
                )}
              </div>

              {enCours ? (
                <div
                  className={`mb-4 p-4 rounded-md bg-[#0F0F0F] border-l-4 ${
                    isTvUrgent(enCours) ? 'animate-pulse-urgent' : ''
                  }`}
                  style={{ borderLeftColor: c.mecanicien.couleur }}
                >
                  <div className="text-3xl font-bold mb-2 truncate flex items-center gap-2">
                    {enCours.urgent && (
                      <span className="text-[#DC2626] text-base font-bold uppercase tracking-wider">
                        🔥 Urgent
                      </span>
                    )}
                    <span className="truncate">{enCours.nature}</span>
                  </div>
                  <div className="text-base text-gray-300">
                    {enCours.moto.modele} · {enCours.moto.vin}
                  </div>
                  <div className="text-sm text-gray-400 mt-1 font-mono">
                    {formatHeureMur(enCours.creneauDebut)} → {formatHeureMur(enCours.creneauFin)}
                  </div>
                </div>
              ) : (
                <div className="mb-4 p-4 rounded-md bg-[#0F0F0F] text-center text-gray-500 text-sm uppercase tracking-wider">
                  En attente de la prochaine intervention
                </div>
              )}

              {aVenir.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">À suivre</div>
                  <div className="space-y-2">
                    {aVenir.map((i) => (
                      <div
                        key={i.id}
                        className={`flex items-center justify-between text-sm py-1.5 px-3 rounded-sm bg-[#0F0F0F] ${
                          isTvUrgent(i) ? 'animate-pulse-urgent' : ''
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate flex items-center gap-1.5">
                            {i.urgent && <span className="text-[#DC2626]">🔥</span>}
                            <span className="truncate">{i.nature}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate">{i.moto.modele}</div>
                        </div>
                        <div className="text-xs font-mono text-gray-400 shrink-0 ml-3">
                          {formatHeureMur(i.creneauDebut)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bandeau bas : arrivées + prêts */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#1A1A1A] rounded-lg p-5 border border-gray-800">
          <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3">🛬 Prochains arrivages</h3>
          {interventions
            .filter((i) => i.statut === 'CONFIRME')
            .slice(0, 4)
            .map((i) => (
              <div key={i.id} className="flex items-center justify-between py-1.5 text-sm">
                <span>
                  <span className="font-medium">{i.moto.modele}</span>{' '}
                  <span className="text-gray-500">· {i.client.prenom} {i.client.nom}</span>
                </span>
                <span className="text-xs font-mono text-gray-400">
                  {formatHeureMur(i.creneauDebut)}
                </span>
              </div>
            ))}
        </div>
        <div className="bg-[#1A1A1A] rounded-lg p-5 border border-gray-800">
          <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3">✅ Prêts à récupérer</h3>
          {interventions
            .filter((i) => i.statut === 'TERMINE')
            .map((i) => (
              <div key={i.id} className="flex items-center justify-between py-1.5 text-sm">
                <span>
                  <span className="font-medium">{i.moto.modele}</span>{' '}
                  <span className="text-gray-500">· {i.client.prenom} {i.client.nom}</span>
                </span>
                <span className="text-xs font-mono text-green-400">PRÊTE</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
